import { useEffect, useState } from "react";

// A drawing's file history: the file on it now and the previous files kept
// (newest 3), each restorable. Restoring archives the current file first, so it
// can itself be undone.

const fmtMb = (b) => (b == null ? "" : `${(b / 1048576).toFixed(1)} MB`);
const fmtDate = (d) => { if (!d) return ""; const x = new Date(d); return Number.isNaN(+x) ? "" : x.toLocaleString(); };

export default function SheetVersionsPanel({ sheet, loadVersions, onRestore, onClose, isLocked }) {
  const [versions, setVersions] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let off = false;
    loadVersions(sheet.SheetID).then(v => { if (!off) setVersions(v); }).catch(e => { if (!off) setError(e.message || "Could not load versions"); });
    return () => { off = true; };
  }, [sheet.SheetID, sheet.VersionNumber, loadVersions]);

  async function restore(v) {
    if (!window.confirm(`Restore version ${v.VersionNumber}${v.FileName ? ` (${v.FileName})` : ""}?\n\nThe file on this drawing now is kept as a version, so you can switch back. The drawing is marked Needs review.`)) return;
    setBusy(true);
    try { await onRestore(sheet.SheetID, v.VersionID); onClose?.(); }
    catch (e) { setError(e.message || "Restore failed"); }
    finally { setBusy(false); }
  }

  return (
    <div role="dialog" aria-label="Drawing versions" style={{ position: "absolute", top: 8, right: 8, zIndex: 26, width: 340, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.22)", fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", padding: "8px 10px", borderBottom: "1px solid var(--cs-border)" }}>
        <strong style={{ flex: 1 }}>Versions of “{sheet.SheetName}”</strong>
        <button type="button" onClick={onClose} aria-label="Close versions" style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: "var(--text3)" }}>✕</button>
      </div>
      <div style={{ padding: "8px 10px" }}>
        <div style={{ fontWeight: 700 }}>Version {sheet.VersionNumber || 1} — current</div>
        <div style={{ color: "var(--text3)" }}>{sheet.FileName || "(file name not recorded)"}{sheet.ReplacedAt ? ` · replaced ${fmtDate(sheet.ReplacedAt)}` : ""}</div>
      </div>
      <div style={{ padding: "0 10px 8px" }}>
        {error && <div role="alert" style={{ color: "#b91c1c" }}>{error}</div>}
        {versions === null && !error && <div style={{ color: "var(--text3)" }}>Loading…</div>}
        {versions && versions.length === 0 && <div style={{ color: "var(--text3)" }}>No earlier versions — a replaced file is kept here (the newest 3).</div>}
        {versions && versions.map(v => (
          <div key={v.VersionID} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid var(--cs-border)" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>Version {v.VersionNumber}</div>
              <div style={{ color: "var(--text3)" }}>{v.FileName || "(file name not recorded)"} · {fmtMb(v.SizeBytes)} · kept {fmtDate(v.ArchivedAt)}</div>
            </div>
            <button type="button" disabled={busy || isLocked} onClick={() => restore(v)}
              style={{ fontSize: 11.5, padding: "3px 10px", border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", cursor: busy || isLocked ? "default" : "pointer", color: "var(--text2)" }}>Restore</button>
          </div>
        ))}
      </div>
    </div>
  );
}
